/**
 * Servicio para obtener un pedido por número de guía.
 * Usa GET /list?guia=XXX&limit=1 y, si hay resultado, GET /list/:id para el detalle completo.
 */
import axiosInstance from '../../api/axiosInstance'

const MIN_GUIA_LENGTH = 5
const SPECIAL_PREFIX = '03005'
const THREE_DIGIT_SUFFIX = /^\d{3}$/

function normalizeGuia(rawGuia: string): string {
  const trimmed = rawGuia.trim()

  // Patrón 1: 03005......XXX (cortar primeros 11 y sufijo de 3 dígitos: 001, 002, 003, 004...)
  if (
    trimmed.startsWith(SPECIAL_PREFIX) &&
    trimmed.length >= 14 &&
    THREE_DIGIT_SUFFIX.test(trimmed.slice(-3))
  ) {
    const withoutPrefix = trimmed.substring(11).trim()
    return withoutPrefix.length >= 3 ? withoutPrefix.slice(0, -3) : withoutPrefix
  }

  // Patrón 2: 7 + [base en BD] + sufijo 001/002/003... (longitud 15)
  if (trimmed.length === 15 && trimmed.startsWith('7')) {
    return trimmed.substring(1, trimmed.length - 3)
  }

  return trimmed
}

/** Componente de un kit (producto dentro de un combo). */
export interface KitComponent {
  id_combo?: number
  sku: string
  name: string
  quantity: number
  price?: string
  tax_rate?: string
  [key: string]: unknown
}

/** Línea de pedido; puede ser producto simple o kit con components. */
export interface LineItemFromGuia {
  product_id: string
  product_name: string
  quantity: number
  price?: string
  is_combo?: number | boolean
  components?: KitComponent[]
  [key: string]: unknown
}

export interface OrderDetailFromGuia {
  id: number
  orders_id: number
  origin_order_id?: number
  guia?: string
  extraDetails?: { transportadora?: string; [key: string]: unknown }
  customer?: { first_name: string; last_name: string; [key: string]: unknown }
  lineItems?: LineItemFromGuia[]
  [key: string]: unknown
}

/**
 * Obtiene el detalle completo de un pedido por número de guía.
 * 1. Llama GET /list?guia=${guia}&limit=1
 * 2. Si la respuesta trae el pedido completo (lineItems, customer), lo devuelve
 * 3. Si trae solo lista resumida, toma el id del primer ítem y llama GET /list/${id}
 */
export async function getOrderByGuia(guia: string): Promise<OrderDetailFromGuia | null> {
  const normalized = normalizeGuia(guia)
  if (normalized.length < MIN_GUIA_LENGTH) {
    return null
  }

  const token = localStorage.getItem('accessToken')
  const listResponse = await axiosInstance.get<{ data: unknown[]; meta?: unknown }>(
    `/list?guia=${encodeURIComponent(normalized)}&limit=1`,
    { headers: { Authorization: `Bearer ${token}` } }
  )

  const data = listResponse.data?.data
  if (!Array.isArray(data) || data.length === 0) {
    return null
  }

  const first = data[0] as { id?: number; lineItems?: unknown; customer?: unknown }
  const hasFullDetail =
    first &&
    typeof first === 'object' &&
    Array.isArray(first.lineItems) &&
    first.customer != null

  if (hasFullDetail) {
    return first as OrderDetailFromGuia
  }

  const orderId = first.id
  if (orderId == null || typeof orderId !== 'number') {
    return null
  }

  const detailResponse = await axiosInstance.get<OrderDetailFromGuia>(`/list/${orderId}`, {
    headers: { Authorization: `Bearer ${token}` }
  })

  return detailResponse.data ?? null
}
